import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import toast, { Toaster } from 'react-hot-toast';
import API_URL from '../../config/api';

// دالة لعرض توست نجاح
const toastSuccess = (message) => {
  toast.success(message, {
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#000',
      border: '1px solid #e5e5e5',
      padding: '16px',
      borderRadius: '0px',
      fontSize: '14px',
      fontWeight: 'normal',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    iconTheme: {
      primary: '#000',
      secondary: '#fff',
    },
  });
};

// دالة لعرض توست خطأ
const toastError = (message) => {
  toast.error(message, {
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#dc2626',
      border: '1px solid #e5e5e5',
      padding: '16px',
      borderRadius: '0px',
      fontSize: '14px',
      fontWeight: 'normal',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    iconTheme: {
      primary: '#dc2626',
      secondary: '#fff',
    },
  });
};

const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      rememberMe: false
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const response = await fetch(`${API_URL}/api/auth/login`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: values.email,
            password: values.password,
            rememberMe: values.rememberMe 
          })
        });

        const result = await response.json();

        console.log('Full login response:', result);
        
        if (response.ok) {
          // عرض رسالة النجاح من الباك إند
          toastSuccess(result.message || 'Login successful');
          
          if (result.success && result.data) {
            const { token, user } = result.data;
            
            if (token && user) {
              const userData = {
                token: token,
                user: user
              };
              
              login(userData, values.rememberMe);
              
              // تأخير التنقل لمشاهدة التوست
              setTimeout(() => {
                navigate('/dashboard');
              }, 1500);
              return; 
            }
          }
          
          if (result.token && result.user) {
            const { token, user } = result;
            
            const userData = {
              token: token,
              user: user
            };
            
            login(userData, values.rememberMe);
            
            setTimeout(() => {
              navigate('/dashboard');
            }, 1500);
            return;
          }
          
          // إذا وصلنا هنا، فهناك مشكلة في هيكل البيانات
          formik.setFieldError('password', 'Invalid response structure from server. Please contact support.');
          console.error('Invalid response structure:', result);
          
        } else {
          // عرض رسالة الخطأ من الباك إند
          const errorMessage = result.message || (response.status === 401 ? 'Invalid email or password' : `Server error: ${response.status}`);
          toastError(errorMessage);
          
          if (response.status === 401) {
            formik.setFieldError('password', result.message || 'Invalid email or password');
          } else {
            formik.setFieldError('password', errorMessage);
          }
        }
      } catch (error) {
        console.error('Login error:', error);
        const errorMsg = 'Network error. Please check your connection and try again.';
        toastError(errorMsg);
        formik.setFieldError('password', errorMsg);
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <div className="flex min-h-screen">
      {/* Toaster Component */}
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#fff',
            color: '#000',
            border: '1px solid #e5e5e5',
            padding: '16px',
            borderRadius: '0px',
            fontSize: '14px',
            fontWeight: 'normal',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#000',
              secondary: '#fff',
            },
          },
          error: {
            style: {
              background: '#fff',
              color: '#dc2626',
            },
            iconTheme: {
              primary: '#dc2626',
              secondary: '#fff',
            },
          },
        }}
      />
      
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-[440px]">
          <div className="mb-12">
            <div className="mb-12">
              <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                Welcome
              </h2>
              <p className="text-sm text-gray-600 font-normal tracking-wide">
                Access your collection
              </p>
            </div>
            
            <form onSubmit={formik.handleSubmit} className="space-y-8">
              <div>
                <label htmlFor="login-email" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                  Email Address
                </label>
                <input
                  type="email"
                  id="login-email"
                  name="email"
                  placeholder="your@email.com"
                  className={`w-full px-4 py-3 border text-sm font-normal placeholder:text-gray-400 transition-colors ${
                    formik.touched.email && formik.errors.email
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-gray-300'
                  } focus:outline-none`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
                )}
              </div>

              <div>
                <label htmlFor="login-password" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                  Password
                </label>
                <input
                  type="password"
                  id="login-password"
                  name="password"
                  placeholder="Enter your password"
                  className={`w-full px-4 py-3 border text-sm font-normal placeholder:text-gray-400 transition-colors ${
                    formik.touched.password && formik.errors.password
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-gray-300'
                  } focus:outline-none`}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                />
                {formik.touched.password && formik.errors.password && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.password}</div>
                )}
              </div>

              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="rememberMe"
                    className="w-4 h-4 border-gray-300 text-black focus:ring-0 focus:ring-offset-0"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    checked={formik.values.rememberMe}
                  />
                  <span className="ml-2.5 text-sm text-gray-700 font-normal tracking-wide">
                    Remember me
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-gray-700 hover:text-black transition-colors font-normal tracking-wide underline"
                >
                  Forgot password?
                </Link>
              </div>

              <div className="pt-8">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-black text-white py-3 hover:bg-gray-800 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
              </div>
            </form>

            <div className="mt-12 text-center">
              <p className="text-sm text-gray-600 font-normal tracking-wide">
                New to ArtRewards?
                <Link to="/signup" className="text-black hover:text-gray-600 transition-colors font-normal ml-1 underline">
                  Create an account
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;